/**
 * MonaJ Animations & Interactions
 * - Scroll-triggered fade-in animations
 * - Hero slider touch/swipe support
 * - Smooth section reveals
 */
(function() {
  'use strict';

  /* ================================================
     Scroll-triggered fade-in (IntersectionObserver)
     ================================================ */
  function initScrollAnimations() {
    var fadeEls = document.querySelectorAll('.monaj-fade-in');
    if (!fadeEls.length) return;

    if (!('IntersectionObserver' in window)) {
      fadeEls.forEach(function(el) { el.classList.add('is-visible'); });
      return;
    }

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -40px 0px'
    });

    fadeEls.forEach(function(el) { observer.observe(el); });
  }

  /* ================================================
     Staggered children animation
     ================================================ */
  function initStaggerAnimations() {
    var staggerContainers = document.querySelectorAll('[data-monaj-stagger]');
    if (!staggerContainers.length) return;

    if (!('IntersectionObserver' in window)) {
      staggerContainers.forEach(function(container) {
        var children = container.children;
        for (var i = 0; i < children.length; i++) {
          children[i].classList.add('is-visible');
        }
      });
      return;
    }

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          var children = entry.target.children;
          for (var i = 0; i < children.length; i++) {
            (function(child, delay) {
              setTimeout(function() {
                child.classList.add('is-visible');
              }, delay);
            })(children[i], i * 100);
          }
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });

    staggerContainers.forEach(function(container) { observer.observe(container); });
  }

  /* ================================================
     Hero slider touch/swipe support
     ================================================ */
  function initHeroSwipe() {
    var slider = document.querySelector('[data-monaj-hero-slider]');
    if (!slider) return;

    var startX = 0;
    var endX = 0;
    var threshold = 50;

    slider.addEventListener('touchstart', function(e) {
      startX = e.changedTouches[0].screenX;
    }, { passive: true });

    slider.addEventListener('touchend', function(e) {
      endX = e.changedTouches[0].screenX;
      var diff = startX - endX;

      if (Math.abs(diff) > threshold) {
        var slides = slider.querySelectorAll('[data-slide-index]');
        var dots = slider.querySelectorAll('[data-slide-dot]');
        var currentSlide = 0;

        slides.forEach(function(s, i) {
          if (s.classList.contains('opacity-100')) currentSlide = i;
        });

        var nextSlide;
        if (diff > 0) {
          nextSlide = (currentSlide + 1) % slides.length;
        } else {
          nextSlide = (currentSlide - 1 + slides.length) % slides.length;
        }

        slides.forEach(function(s, i) {
          s.classList.toggle('opacity-100', i === nextSlide);
          s.classList.toggle('opacity-0', i !== nextSlide);
        });
        dots.forEach(function(d, i) {
          d.classList.toggle('bg-white', i === nextSlide);
          d.classList.toggle('scale-110', i === nextSlide);
          d.classList.toggle('bg-white/50', i !== nextSlide);
        });
      }
    }, { passive: true });
  }

  /* ================================================
     Testimonial slider touch support
     ================================================ */
  function initTestimonialSwipe() {
    var sliders = document.querySelectorAll('[data-monaj-testimonials]');
    sliders.forEach(function(slider) {
      var startX = 0;
      var threshold = 50;

      slider.addEventListener('touchstart', function(e) {
        startX = e.changedTouches[0].screenX;
      }, { passive: true });

      slider.addEventListener('touchend', function(e) {
        var diff = startX - e.changedTouches[0].screenX;
        if (Math.abs(diff) > threshold) {
          var btn = diff > 0
            ? slider.querySelector('[data-testimonial-next]')
            : slider.querySelector('[data-testimonial-prev]');
          if (btn) btn.click();
        }
      }, { passive: true });
    });
  }

  /* ================================================
     Collapsible footer on mobile
     ================================================ */
  function initFooterAccordion() {
    var footerAccordions = document.querySelectorAll('[data-monaj-footer-accordion]');
    footerAccordions.forEach(function(acc) {
      var trigger = acc.querySelector('[data-accordion-trigger]');
      var content = acc.querySelector('[data-accordion-content]');
      if (!trigger || !content) return;

      // Only collapsible on mobile
      function checkMobile() {
        if (window.innerWidth < 768) {
          content.style.maxHeight = '0';
          content.style.overflow = 'hidden';
          content.style.transition = 'max-height 0.3s ease';
          trigger.setAttribute('aria-expanded', 'false');
          trigger.style.cursor = 'pointer';
        } else {
          content.style.maxHeight = 'none';
          content.style.overflow = 'visible';
          trigger.setAttribute('aria-expanded', 'true');
          trigger.style.cursor = 'default';
        }
      }

      trigger.addEventListener('click', function() {
        if (window.innerWidth >= 768) return;
        var expanded = trigger.getAttribute('aria-expanded') === 'true';
        if (expanded) {
          content.style.maxHeight = '0';
          trigger.setAttribute('aria-expanded', 'false');
        } else {
          content.style.maxHeight = content.scrollHeight + 'px';
          trigger.setAttribute('aria-expanded', 'true');
        }
      });

      checkMobile();
      window.addEventListener('resize', checkMobile);
    });
  }

  /* ================================================
     Header shrink on scroll
     ================================================ */
  function initHeaderShrink() {
    var header = document.querySelector('[data-monaj-header]');
    if (!header) return;

    var scrollThreshold = 100;
    var isShrunken = false;

    function checkScroll() {
      var shouldShrink = window.scrollY > scrollThreshold;
      if (shouldShrink !== isShrunken) {
        isShrunken = shouldShrink;
        if (shouldShrink) {
          header.classList.add('monaj-header-shrunk');
        } else {
          header.classList.remove('monaj-header-shrunk');
        }
      }
    }

    window.addEventListener('scroll', checkScroll, { passive: true });
    checkScroll();
  }

  /* ================================================
     Initialize all
     ================================================ */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    initScrollAnimations();
    initStaggerAnimations();
    initHeroSwipe();
    initTestimonialSwipe();
    initFooterAccordion();
    initHeaderShrink();
  }
})();
